import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';

import { cn } from '@/lib/cn';
import { controlClassName, controlTone, describedBy, FieldWrapper } from './field';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: ReactNode;
  /** Server or client validation message. Replaces `hint` when present. */
  error?: ReactNode;
  hint?: ReactNode;
  /** Element rendered inside the field on the right (a show/hide toggle, a unit). */
  trailing?: ReactNode;
  /** Element rendered inside the field on the left (an icon). */
  leading?: ReactNode;
  containerClassName?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    id,
    label,
    error,
    hint,
    className,
    containerClassName,
    required,
    trailing,
    leading,
    type = 'text',
    ...props
  },
  ref,
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const hasError = Boolean(error);

  return (
    <FieldWrapper
      id={inputId}
      label={label}
      error={error}
      hint={hint}
      required={required}
      className={containerClassName}
    >
      <div className="relative flex items-center">
        {leading ? (
          <span className="pointer-events-none absolute left-3 flex items-center text-muted">
            {leading}
          </span>
        ) : null}
        <input
          ref={ref}
          id={inputId}
          type={type}
          required={required}
          aria-invalid={hasError || undefined}
          aria-describedby={describedBy(inputId, { hint, error })}
          className={cn(
            controlClassName,
            controlTone(hasError),
            // 44px tall: this form gets filled in on a phone.
            'h-11 px-3 text-base sm:text-sm',
            leading ? 'pl-10' : undefined,
            trailing ? 'pr-11' : undefined,
            className,
          )}
          {...props}
        />
        {trailing ? (
          <span className="absolute right-1 flex items-center">{trailing}</span>
        ) : null}
      </div>
    </FieldWrapper>
  );
});
